import {
  API_BATCH_CONCURRENCY,
  CACHE_USAGE_THRESHOLD,
  COMBINED_PDF_CHUNK_SIZE,
  COMBINED_PDF_CHUNK_SIZE_LARGE,
  COMBINED_PDF_CHUNK_THRESHOLD,
  DEFAULT_PAPER_SIZE,
  FETCH_TIMEOUT_MS,
  FIT_CONCURRENCY_CHUNKED,
  LARGE_DATASET_THRESHOLD,
  MAX_PDF_FETCH_RETRIES,
  PAPER_SIZES,
  PDF_CACHE_CLEANUP_INTERVAL_MS,
  PDF_CACHE_MAX_AGE_LARGE_MS,
  PDF_CACHE_MAX_AGE_SMALL_MS,
  PDF_CACHE_MAX_SIZE_LARGE,
  PDF_CACHE_MAX_SIZE_SMALL,
  PDF_MARGIN_DEFAULT,
  USE_STREAMING_THRESHOLD,
} from "@client-shared/lib/constants";
import { PackingSlipGenerator } from "@client-shared/lib/packingSlipGenerator";
import { SimplePdfMerger } from "@client-shared/lib/pdfMerger";
import {
  fitPdfInMemory,
  fitPdfToPaper,
  fitPdfToPaperFromArrayBuffer,
  serializedCopyPages,
  serializedPdfCreate,
} from "@client-shared/lib/pdfPageFitter";
import { fetchPdfArrayBuffer } from "@client-shared/lib/pdfFetchUtils";
import {
  buildCachedLabelUrlMap,
  buildOrderBatches,
  buildTabs,
  type CachedLabelUrlEntry,
  clearError,
  createInterleavedSequence,
  createOrderIndexMap,
  createOrdersCacheKey,
  createSerializedAddPage,
  createSerializedFitPdfInMemory,
  type FitPdfInMemoryFn,
  extractErrorMessage,
  generateCacheKey,
  getBatchSize,
  getCachedLabelUrls as getCachedLabelUrlsUtil,
  getInitialTab,
  getPackingSlipOptions,
  isAborted,
  isLabelUrlEntryFresh,
  maybeClearExpiredCachedLabelUrls as maybeClearExpiredUtil,
  processAllLabelUrlBatches,
  processBatch,
  processBatchResults,
  processLabelAndSlipForCombined,
  processUrlsWithConcurrency,
  shouldRefreshPresignedLabelUrlsForError,
  shouldShowGroupBy as shouldShowGroupByUtil,
  shouldShowManifest as shouldShowManifestUtil,
} from "@client-shared/lib/shippingDocuments";
import {
  createPdfWrapperUrl,
  downloadBlobWithFilename,
  generateShippingDocumentFilename,
  groupPurchaseOrders,
} from "@client-shared/lib/shippingDocumentsUtils";
import { usePdfCache } from "@client-shared/lib/usePdfCache";
import type { PaperSize } from "@client-shared/types/pdfPrint";
import type { GroupOption, Locale, PurchaseOrder, ShippingDocumentsTabType } from "@shared/types";
import { chunk } from "@shared/util/chunk";
import { PDFDocument, PDFPage } from "pdf-lib";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

type ButtonState = "loading" | "pressed" | undefined;

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

export interface ShippingDocumentsConfig {
  defaultPaperSize?: PaperSize;
  defaultGroupBySettings?: GroupOption[];
  vendorLocale: Locale;
  vendorName?: string;
  sltCustomerAccountNumber?: string;
  customerAccountCarrierDetails?: Array<{
    customerAccountNumber: string;
    isManifestEnabled?: boolean;
  }>;
  translateGrindType: (value: string | undefined) => string | undefined;
  onErrorToast: (message: string) => void;
  onWarningToast: (title: string, description: string) => void;
  /** Stamps the order's PO number in a bar at the bottom of each shipping label. */
  stampPoNumberOnLabels?: boolean;
}

export interface UseShippingDocumentsParams {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrders?: PurchaseOrder[];
  manifestPdfUrl?: string;
  initialTab?: ShippingDocumentsTabType;
  config: ShippingDocumentsConfig;
}

export interface UseShippingDocumentsResult {
  // State
  activeTab: ShippingDocumentsTabType;
  selectedPaperSize: PaperSize;
  pdfUrl: string | null;
  pdfDisplayUrl: string | null;
  error: string | null;
  isLoading: boolean;
  isChangingTab: boolean;
  isChangingPaperSize: boolean;
  showPreviewFallback: boolean;
  isLargeDataset: boolean;
  filename: string;
  tabs: Array<{ id: ShippingDocumentsTabType; label: string; disabled: boolean }>;
  isMulti: boolean;
  showGroupBy: boolean;
  selectedGroupOptions: Set<GroupOption>;
  hasLabels: boolean;
  hasCancelledOrder: boolean;
  paperSizes: readonly PaperSize[];

  // Actions
  handleTabChange: (tab: ShippingDocumentsTabType) => Promise<void>;
  handlePaperSizeChange: (size: PaperSize) => Promise<void>;
  handleGroupOptionToggle: (option: GroupOption) => void;
  handleDownload: () => void;
  handleOpenInNewTab: () => void;
  handleClose: () => void;
  getPaperSizeButtonState: (size: PaperSize) => ButtonState;
  getGroupByButtonState: (option: GroupOption) => ButtonState;
  /** Call this from the iframe's onLoad handler to schedule revocation of old blob URLs. */
  handleIframeLoad: (currentSrc: string) => void;
  /** Call this from the iframe's onError handler to show the fallback. */
  handleIframeError: () => void;
}

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Module-level helpers (no closures needed)
// ---------------------------------------------------------------------------

function validatePdfDocumentWithPageIndices(pdfDoc: unknown, context: string): asserts pdfDoc is PDFDocument {
  if (pdfDoc === null || typeof pdfDoc !== "object" || typeof (pdfDoc as PDFDocument).getPageIndices !== "function") {
    throw new TypeError(`${context}: ${typeof pdfDoc}`);
  }
}

function validatePageIndicesArr(pageIndices: number[], context: string): void {
  if (!Array.isArray(pageIndices)) {
    throw new TypeError(`${context}: page indices is not an array (${typeof pageIndices})`);
  }
  for (const idx of pageIndices) {
    if (typeof idx !== "number" || !Number.isFinite(idx) || idx < 0) {
      throw new TypeError(`${context}: Invalid page index ${idx} (type: ${typeof idx})`);
    }
  }
}

function getValidatedPageIndices(pdfDoc: PDFDocument, context: string): number[] {
  try {
    const pageIndices = pdfDoc.getPageIndices();
    validatePageIndicesArr(pageIndices, context);
    return pageIndices;
  } catch (err) {
    throw new TypeError(`${context}: ${err instanceof Error ? err.message : String(err)}`);
  }
}

async function processStreamingLabelPdf(params: {
  url: string;
  mergedPdf: PDFDocument;
  paperSize: PaperSize;
  signal?: AbortSignal;
  poNumber?: string;
}): Promise<void> {
  const { url, mergedPdf, paperSize, signal, poNumber } = params;
  if (signal?.aborted) {
    return;
  }
  try {
    const srcPdf = await SimplePdfMerger.fetchPdfWithRetry(url, {
      timeout: FETCH_TIMEOUT_MS,
      maxRetries: MAX_PDF_FETCH_RETRIES,
      signal,
    });
    if (!srcPdf || signal?.aborted) {
      return;
    }

    validatePdfDocumentWithPageIndices(srcPdf, `Invalid PDFDocument from fetchPdfWithRetry for ${url}`);
    getValidatedPageIndices(srcPdf, `Corrupted PDF from ${url}`);

    const fittedPdf = await fitPdfInMemory(srcPdf, paperSize, PDF_MARGIN_DEFAULT, poNumber);
    validatePdfDocumentWithPageIndices(fittedPdf, "fitPdfInMemory returned invalid PDFDocument");
    const pageIndices = getValidatedPageIndices(fittedPdf, "fitPdfInMemory returned invalid page indices");
    const pages = await serializedCopyPages(mergedPdf, fittedPdf, pageIndices);
    for (const page of pages) {
      mergedPdf.addPage(page);
    }
  } catch (error) {
    if (!signal?.aborted && !(error instanceof Error && error.name === "AbortError")) {
      console.error("Error processing PDF:", error);
    }
  }
}

function fetchBatchGroup(
  batches: Parameters<typeof processBatch>[0][],
  startIndex: number,
  getBulkShippingLabelUrls: Parameters<typeof processBatch>[2],
  signal?: AbortSignal
) {
  return Promise.all(
    batches
      .slice(startIndex, startIndex + API_BATCH_CONCURRENCY)
      .map((batch, batchIndex) => processBatch(batch, startIndex + batchIndex + 1, getBulkShippingLabelUrls, signal))
  );
}

function* extractIndexedLabelUrls(
  results: ReadonlyArray<{ urlMap: Record<string, string | null>; batch: ReadonlyArray<{ purchaseOrderId: string }> }>,
  orderIndexMap: ReadonlyMap<string, number>
): Generator<{ url: string; orderIndex: number }> {
  for (const result of results) {
    for (const orderId of result.batch) {
      const url = result.urlMap[orderId.purchaseOrderId];
      if (!url) {
        continue;
      }
      const orderIndex = orderIndexMap.get(orderId.purchaseOrderId);
      if (orderIndex !== undefined) {
        yield { url, orderIndex };
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useShippingDocuments = (params: UseShippingDocumentsParams): UseShippingDocumentsResult => {
  const { open, onOpenChange, purchaseOrders, manifestPdfUrl, initialTab = "combined", config } = params;
  const { t } = useTranslation();

  // Derived values from config
  const vendorDefaultPaper: PaperSize | undefined = useMemo(() => {
    const raw = config.defaultPaperSize;
    return raw === "A4" || raw === "A6" || raw === "Letter" || raw === "4x6" || raw === "3x4" ? raw : undefined;
  }, [config.defaultPaperSize]);

  const normalizedOrders: PurchaseOrder[] = useMemo(() => purchaseOrders ?? [], [purchaseOrders]);

  const hasLabels = useMemo(() => normalizedOrders.some((o) => o.beanzConnectLabelPrintEnabled), [normalizedOrders]);
  const hasCancelledOrder = useMemo(() => normalizedOrders.some((o) => o.orderStatus === "Canceled"), [normalizedOrders]);
  const isLargeDataset = useMemo(() => normalizedOrders.length >= LARGE_DATASET_THRESHOLD, [normalizedOrders.length]);

  const [activeTab, setActiveTab] = useState<ShippingDocumentsTabType>(() =>
    getInitialTab(hasCancelledOrder, hasLabels, initialTab ?? "packing-slip")
  );
  const [selectedPaperSize, setSelectedPaperSize] = useState<PaperSize>(() => vendorDefaultPaper ?? DEFAULT_PAPER_SIZE);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [changingPaperSize, setChangingPaperSize] = useState<PaperSize | null>(null);
  const [changingGroupBy, setChangingGroupBy] = useState<GroupOption | null>(null);
  const [changingTab, setChangingTab] = useState<ShippingDocumentsTabType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedPreviewUrl, setFailedPreviewUrl] = useState<string | null>(null);
  const [selectedGroupOptions, setSelectedGroupOptions] = useState<Set<GroupOption>>(() => {
    return config.defaultGroupBySettings ? new Set(config.defaultGroupBySettings) : new Set();
  });

  // Refs
  const cachedLabelUrlsRef = useRef<Record<string, CachedLabelUrlEntry>>({});
  const labelUrlsCacheKeyRef = useRef<string>("");
  // Source label URL → purchaseOrderId. The fitters only receive a URL, so this is how a
  // label's PO number is recovered at stamping time.
  const labelUrlToPoRef = useRef<Map<string, string>>(new Map());
  const previousOrdersCacheKeyRef = useRef<string | undefined>(undefined);
  const pendingRevocationRef = useRef<Set<string>>(new Set());
  const pendingWrapperRevocationRef = useRef<Set<string>>(new Set());
  const currentWrapperUrlRef = useRef<string | null>(null);
  const currentDisplayedUrlRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeTabRef = useRef(activeTab ?? "packing-slip");
  const initializedRef = useRef(false);
  const generateInitialPdfRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Holds the source bytes for the *current* manifest only — a single entry, not a
  // growing cache. It's overwritten whenever the manifest URL changes (different
  // batch), so bytes never accumulate across manifests. The pre-signed URL is
  // short-lived (~60s), so we fetch the bytes once and re-fit them on every
  // paper-size change rather than re-fetching the (soon-to-expire) URL — which
  // would otherwise return HTTP 403 once it lapses.
  const manifestBytesRef = useRef<{ url: string; bytes: ArrayBuffer } | null>(null);
  // Monotonic counter for manifest fit requests. Paper-size buttons stay enabled
  // while a manifest is still being fetched/fitted, so several fits can be in
  // flight at once; only the most recently requested one may publish its result.
  const manifestFitSequenceRef = useRef(0);
  // Mirror of pdfUrl so the unmount cleanup can revoke the current blob without
  // closing over stale state.
  const pdfUrlRef = useRef<string | null>(null);

  // PDF cache
  const { getCachedPdf, setCachedPdf, clearCache, forceCleanup, hasCachedUrl } = usePdfCache({
    maxAge: isLargeDataset ? PDF_CACHE_MAX_AGE_LARGE_MS : PDF_CACHE_MAX_AGE_SMALL_MS,
    maxSize: isLargeDataset ? PDF_CACHE_MAX_SIZE_LARGE : PDF_CACHE_MAX_SIZE_SMALL,
  });

  const isMulti = useMemo(() => normalizedOrders.length > 1, [normalizedOrders.length]);

  const processedOrders = useMemo(() => {
    if (!isMulti) {
      return normalizedOrders;
    }
    return groupPurchaseOrders(normalizedOrders, selectedGroupOptions);
  }, [normalizedOrders, isMulti, selectedGroupOptions]);

  const ordersCacheKey = useMemo(() => createOrdersCacheKey(normalizedOrders), [normalizedOrders]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    if (!isLargeDataset) {
      return;
    }
    const cleanupInterval = setInterval(() => {
      forceCleanup();
    }, PDF_CACHE_CLEANUP_INTERVAL_MS);
    return () => clearInterval(cleanupInterval);
  }, [isLargeDataset, forceCleanup]);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return () => {
      controller.abort();
    };
  }, [open, ordersCacheKey]);

  useEffect(() => {
    if (initializedRef.current) {
      return;
    }
    if (vendorDefaultPaper) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- initialize from async-loaded vendor prop only while this hook instance is still uninitialized
      setSelectedPaperSize(vendorDefaultPaper);
    }
  }, [vendorDefaultPaper]);

  useEffect(() => {
    if (!initializedRef.current && open) {
      setActiveTab(getInitialTab(hasCancelledOrder, hasLabels, initialTab ?? "packing-slip"));
      initializedRef.current = true;
    } else if (!open) {
      initializedRef.current = false;
    }
  }, [open, initialTab, hasLabels, hasCancelledOrder]);

  useEffect(() => {
    if (!open) {
      cachedLabelUrlsRef.current = {};
      labelUrlsCacheKeyRef.current = "";
      previousOrdersCacheKeyRef.current = undefined;
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      previousOrdersCacheKeyRef.current = undefined;
      return;
    }
    const currentCacheKey = ordersCacheKey;
    const previousCacheKey = previousOrdersCacheKeyRef.current;
    if (previousCacheKey !== undefined && currentCacheKey !== previousCacheKey) {
      cachedLabelUrlsRef.current = {};
      labelUrlsCacheKeyRef.current = "";
      labelUrlToPoRef.current.clear();
      clearCache();
      setPdfUrl(null);
    }
    previousOrdersCacheKeyRef.current = currentCacheKey;
  }, [ordersCacheKey, open, clearCache]);

  useEffect(() => {
    pdfUrlRef.current = pdfUrl;
  }, [pdfUrl]);

  // Revokes every blob URL this hook instance still owns: the displayed PDF, its
  // wrapper, and anything queued for deferred revocation. Shared by the close
  // effect and the unmount cleanup so a host that unmounts the dialog instead of
  // rendering it closed releases the same memory.
  const releaseBlobUrls = useCallback((currentPdfUrl: string | null) => {
    const urlsToRevoke = new Set<string>([...pendingRevocationRef.current, ...pendingWrapperRevocationRef.current]);
    if (currentPdfUrl) {
      urlsToRevoke.add(currentPdfUrl);
    }
    if (currentWrapperUrlRef.current) {
      urlsToRevoke.add(currentWrapperUrlRef.current);
    }
    for (const url of urlsToRevoke) {
      if (url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    }
    pendingRevocationRef.current.clear();
    pendingWrapperRevocationRef.current.clear();
    currentWrapperUrlRef.current = null;
    currentDisplayedUrlRef.current = null;
  }, []);

  // Blob URL revocation on close
  useEffect(() => {
    if (!open) {
      releaseBlobUrls(pdfUrl);
      if (pdfUrl?.startsWith("blob:")) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- clears pdfUrl alongside blob URL revocation when dialog closes
        setPdfUrl(null);
      }
      // Clear stale failure flag so reopens with a cached URL don't show the previous session's fallback.
      setFailedPreviewUrl(null);
    }
  }, [open, pdfUrl, releaseBlobUrls]);

  // Blob URL revocation on unmount. Hosts may unmount the dialog the moment it
  // closes, so the close effect above never observes `open === false`; without
  // this, the fitted manifest (which is never added to the PDF cache) and its
  // wrapper would be retained for the lifetime of the page.
  const unmountCleanupRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    unmountCleanupRef.current = () => {
      releaseBlobUrls(pdfUrlRef.current);
      clearCache();
    };
  }, [releaseBlobUrls, clearCache]);
  useEffect(() => () => unmountCleanupRef.current(), []);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const handleClose = useCallback(() => {
    clearCache();
    onOpenChange(false);
  }, [onOpenChange, clearCache]);

  // generateCacheKey is imported from cacheKeyUtils

  const getPackingSlipOptionsForOrder = useCallback(
    (order: PurchaseOrder) => {
      return getPackingSlipOptions(order, {
        translateGrindType: config.translateGrindType,
        sltCustomerAccountNumber: config.sltCustomerAccountNumber,
        vendorName: config.vendorName,
      });
    },
    [config.translateGrindType, config.sltCustomerAccountNumber, config.vendorName]
  );

  const generatePackingSlip = useCallback(
    async (order: PurchaseOrder, paperSize: PaperSize): Promise<string> => {
      const options = getPackingSlipOptionsForOrder(order);
      return await PackingSlipGenerator.createPackingSlip(order, paperSize, order.promotionTemplate, options);
    },
    [getPackingSlipOptionsForOrder]
  );

  const generateAllSlips = useCallback(
    async (orders: PurchaseOrder[], paperSize: PaperSize): Promise<string[]> => {
      if (orders.length === 0) {
        return [];
      }
      const batchSize = getBatchSize(orders.length, false);
      const urls: string[] = [];
      for (let i = 0; i < orders.length; i += batchSize) {
        const batch = orders.slice(i, i + batchSize);
        // Batches are bounded to limit concurrent API calls and memory use.
        const batchUrls = await Promise.all(batch.map((o) => generatePackingSlip(o, paperSize))); // NOSONAR
        urls.push(...batchUrls);
      }
      return urls;
    },
    [generatePackingSlip]
  );

  // isLabelUrlEntryFresh, buildCachedLabelUrlMap, shouldRefreshPresignedLabelUrlsForError
  // are imported from labelUrlFetching utilities.

  const createBatches = useCallback((orderIds: Array<{ purchaseOrderId: string; batchId?: string }>) => buildOrderBatches(orderIds), []);

  const getCachedLabelUrls = useCallback(
    (orders: PurchaseOrder[], cacheKey: string): string[] | null =>
      getCachedLabelUrlsUtil(orders, cacheKey, cachedLabelUrlsRef.current, labelUrlsCacheKeyRef.current, isLabelUrlEntryFresh),
    []
  );

  const clearCachedLabelUrls = useCallback(() => {
    cachedLabelUrlsRef.current = {};
    labelUrlsCacheKeyRef.current = "";
    labelUrlToPoRef.current.clear();
  }, []);

  const rememberLabelUrlPoMappings = useCallback((urlMap: Record<string, string | null>) => {
    for (const [purchaseOrderId, url] of Object.entries(urlMap)) {
      if (url) {
        labelUrlToPoRef.current.set(url, purchaseOrderId);
      }
    }
  }, []);

  const resolvePoNumberForUrl = useCallback(
    (url: string): string | undefined => (config.stampPoNumberOnLabels ? labelUrlToPoRef.current.get(url) : undefined),
    [config.stampPoNumberOnLabels]
  );

  const maybeClearExpiredCachedLabelUrls = useCallback(() => {
    maybeClearExpiredUtil(Object.values(cachedLabelUrlsRef.current), clearCachedLabelUrls);
  }, [clearCachedLabelUrls]);

  // shouldRefreshPresignedLabelUrlsForError is imported from labelUrlFetching utilities

  const setPdfUrlWithRevocation = useCallback(
    (newPdfUrl: string) => {
      setPdfUrl((prevPdfUrl) => {
        if (
          prevPdfUrl?.startsWith("blob:") &&
          prevPdfUrl !== newPdfUrl &&
          prevPdfUrl !== currentDisplayedUrlRef.current &&
          !hasCachedUrl(prevPdfUrl)
        ) {
          pendingRevocationRef.current.add(prevPdfUrl);
        }
        return newPdfUrl;
      });
    },
    [hasCachedUrl]
  );

  const handleManifestPdf = useCallback(
    async (paperSize: PaperSize, signal?: AbortSignal): Promise<void> => {
      if (!manifestPdfUrl) {
        setPdfUrl(null);
        return;
      }
      const fitSequence = ++manifestFitSequenceRef.current;
      const isSuperseded = (): boolean => fitSequence !== manifestFitSequenceRef.current;
      // Fetch the manifest bytes once and reuse them for every paper-size change.
      // The manifest comes from a short-lived pre-signed S3 URL; re-fetching it on
      // each change risks HTTP 403 once the URL expires.
      let cached = manifestBytesRef.current;
      if (cached?.url !== manifestPdfUrl) {
        const bytes = await fetchPdfArrayBuffer(manifestPdfUrl, { signal });
        cached = { url: manifestPdfUrl, bytes };
        manifestBytesRef.current = cached;
      }
      if (isSuperseded()) {
        // A newer paper-size request started while we were fetching; let it own the result.
        return;
      }
      // Pass a copy so pdf-lib never consumes the cached buffer across re-fits.
      const fittedManifest = await fitPdfToPaperFromArrayBuffer(cached.bytes.slice(0), paperSize, PDF_MARGIN_DEFAULT, signal);
      if (signal?.aborted || isSuperseded()) {
        // The fitted blob URL was already created; revoke it so the orphaned
        // object URL doesn't leak when we bail out without displaying it.
        if (fittedManifest.startsWith("blob:")) {
          URL.revokeObjectURL(fittedManifest);
        }
        return;
      }
      setPdfUrlWithRevocation(fittedManifest);
    },
    [manifestPdfUrl, setPdfUrlWithRevocation]
  );

  const handleCaching = useCallback(
    (cacheKey: string, newPdfUrl: string, shouldUseCache: boolean) => {
      setPdfUrlWithRevocation(newPdfUrl);
      if (shouldUseCache) {
        setCachedPdf(cacheKey, newPdfUrl);
      }
    },
    [setCachedPdf, setPdfUrlWithRevocation]
  );

  // ---------------------------------------------------------------------------
  // Label URL fetching
  // ---------------------------------------------------------------------------

  const fetchLabelUrls = useCallback(
    async (orders: PurchaseOrder[], signal?: AbortSignal): Promise<string[]> => {
      if (orders.length === 0) {
        return [];
      }
      const cacheKey = createOrdersCacheKey(orders);
      maybeClearExpiredCachedLabelUrls();
      const cachedUrls = getCachedLabelUrls(orders, cacheKey);
      if (cachedUrls) {
        return cachedUrls;
      }

      try {
        const { getBulkShippingLabelUrls } = await import("@client-shared/services/api/orders");
        const orderIds = orders.map((o) => ({ purchaseOrderId: o.purchaseOrderId, batchId: o.batchId }));
        const batches = createBatches(orderIds);
        const { urlMap, hasErrors } = await processAllLabelUrlBatches(batches, getBulkShippingLabelUrls, signal);
        if (signal?.aborted) {
          return [];
        }
        rememberLabelUrlPoMappings(urlMap);
        const cachedMap = buildCachedLabelUrlMap(urlMap);
        labelUrlsCacheKeyRef.current = cacheKey;
        cachedLabelUrlsRef.current = cachedMap;
        if (hasErrors) {
          config.onErrorToast(t("components.ShippingDocumentsDialog.errorFetchingShippingLabels"));
        }
        return orders.map((o) => urlMap[o.purchaseOrderId]).filter((url): url is string => url !== null && url !== undefined);
      } catch (err) {
        if (signal?.aborted || (err instanceof Error && err.name === "AbortError")) {
          return [];
        }
        console.error("Failed to fetch shipping label URLs:", err);
        config.onErrorToast(t("components.ShippingDocumentsDialog.errorFetchingShippingLabels"));
        return [];
      }
    },
    [config, createBatches, getCachedLabelUrls, maybeClearExpiredCachedLabelUrls, rememberLabelUrlPoMappings, t]
  );

  const fetchLabelUrlsGenerator = useCallback(
    async function* (orders: PurchaseOrder[], cacheKey: string, signal?: AbortSignal): AsyncGenerator<string, void, unknown> {
      if (orders.length === 0) {
        return;
      }
      const cachedUrls = getCachedLabelUrls(orders, cacheKey);
      if (cachedUrls) {
        for (const url of cachedUrls) {
          yield url;
        }
        return;
      }
      try {
        const { getBulkShippingLabelUrls } = await import("@client-shared/services/api/orders");
        const orderIds = orders.map((o) => ({ purchaseOrderId: o.purchaseOrderId, batchId: o.batchId }));
        const batches = createBatches(orderIds);
        const urlMap: Record<string, string | null> = {};
        let hasBatchErrors = false;
        for (let i = 0; i < batches.length; i += API_BATCH_CONCURRENCY) {
          if (signal?.aborted) {
            return;
          }
          // Groups of batches run one after another to bound API concurrency and keep yield order.
          const results = await fetchBatchGroup(batches, i, getBulkShippingLabelUrls, signal); // NOSONAR
          hasBatchErrors = hasBatchErrors || results.some((r) => !r.success);
          // Must run before yielding: consumers start fitting each URL as soon as it is yielded.
          results.forEach((result) => rememberLabelUrlPoMappings(result.urlMap));
          yield* processBatchResults(results, urlMap);
        }
        labelUrlsCacheKeyRef.current = cacheKey;
        cachedLabelUrlsRef.current = buildCachedLabelUrlMap(urlMap);
        if (hasBatchErrors) {
          config.onErrorToast(t("components.ShippingDocumentsDialog.errorFetchingShippingLabels"));
        }
      } catch (err) {
        if (signal?.aborted || (err instanceof Error && err.name === "AbortError")) {
          return;
        }
        console.error("Failed to fetch shipping label URLs:", err);
        config.onErrorToast(t("components.ShippingDocumentsDialog.errorFetchingShippingLabels"));
      }
    },
    [config, createBatches, getCachedLabelUrls, rememberLabelUrlPoMappings, t]
  );

  const fitAllLabels = useCallback(
    async (labels: string[], paperSize: PaperSize, signal?: AbortSignal): Promise<string[]> => {
      if (labels.length === 0 || signal?.aborted) {
        return [];
      }
      const batchSize = getBatchSize(labels.length, true);
      const out: string[] = [];
      for (let i = 0; i < labels.length; i += batchSize) {
        if (signal?.aborted) {
          return out;
        }
        const batch = labels.slice(i, i + batchSize);
        // Batches are bounded to limit browser memory while fitting PDFs.
        const fitRequests = batch.map((url) => fitPdfToPaper(url, paperSize, PDF_MARGIN_DEFAULT, signal, resolvePoNumberForUrl(url)));
        const batchFitted = await Promise.all(fitRequests); // NOSONAR
        out.push(...batchFitted);
      }
      return out;
    },
    [resolvePoNumberForUrl]
  );

  const fetchAndMergeLabelsStreaming = useCallback(
    async (orders: PurchaseOrder[], paperSize: PaperSize, signal?: AbortSignal): Promise<string> => {
      if (!orders || orders.length === 0) {
        throw new Error("Cannot fetch and merge labels: no orders provided.");
      }
      if (signal?.aborted) {
        throw new Error("Operation aborted");
      }
      const mergedPdf = await serializedPdfCreate();
      const cacheKey = createOrdersCacheKey(orders);
      const urlGenerator = fetchLabelUrlsGenerator(orders, cacheKey, signal);
      await processUrlsWithConcurrency(
        urlGenerator,
        async (url: string) => {
          await processStreamingLabelPdf({ url, mergedPdf, paperSize, signal, poNumber: resolvePoNumberForUrl(url) });
        },
        { signal }
      );
      if (signal?.aborted) {
        throw new Error("Operation aborted");
      }
      if (mergedPdf.getPageCount() === 0) {
        throw new Error("No shipping labels could be loaded. Please try again.");
      }
      const pdfBytes = await mergedPdf.save({ useObjectStreams: true });
      const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
      return URL.createObjectURL(blob);
    },
    [fetchLabelUrlsGenerator, resolvePoNumberForUrl]
  );

  // ---------------------------------------------------------------------------
  // PDF generation helpers
  // ---------------------------------------------------------------------------

  const processLargeStreamingBatch = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ): Promise<{ status: "completed" | "aborted" | "fallbackNeeded" }> => {
      try {
        const merged = await fetchAndMergeLabelsStreaming(currentOrders, sizeToUse, signal);
        if (signal?.aborted) {
          return { status: "aborted" };
        }
        handleCaching(cacheKey, merged, shouldUseCache);
        return { status: "completed" };
      } catch (err) {
        if (signal?.aborted || (err instanceof Error && err.message === "Operation aborted")) {
          return { status: "aborted" };
        }
        console.error("Streaming label merge failed, falling back to standard approach:", err);
        config.onWarningToast(
          t("components.ShippingDocumentsDialog.streamingFallbackTitle"),
          t("components.ShippingDocumentsDialog.streamingFallbackDescription")
        );
        return { status: "fallbackNeeded" };
      }
    },
    [config, fetchAndMergeLabelsStreaming, handleCaching, t]
  );

  const processMultiLabelStandard = useCallback(
    async (currentLabelUrls: string[], sizeToUse: PaperSize, cacheKey: string, shouldUseCache: boolean, signal?: AbortSignal) => {
      const fitted = await fitAllLabels(currentLabelUrls, sizeToUse, signal);
      if (signal?.aborted) {
        return;
      }
      const merged = await SimplePdfMerger.mergePdfs(fitted, signal);
      if (signal?.aborted) {
        return;
      }
      handleCaching(cacheKey, merged, shouldUseCache);
    },
    [fitAllLabels, handleCaching]
  );

  const processSingleLabel = useCallback(
    async (labelUrl: string, sizeToUse: PaperSize, cacheKey: string, shouldUseCache: boolean, signal?: AbortSignal) => {
      const fittedSingle = await fitPdfToPaper(labelUrl, sizeToUse, PDF_MARGIN_DEFAULT, signal, resolvePoNumberForUrl(labelUrl));
      if (signal?.aborted) {
        return;
      }
      handleCaching(cacheKey, fittedSingle, shouldUseCache);
    },
    [handleCaching, resolvePoNumberForUrl]
  );

  const generateShippingLabelPdf = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      currentIsMulti: boolean,
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      if (currentOrders.length === 0) {
        setPdfUrl(null);
        return;
      }
      if (signal?.aborted) {
        return;
      }
      if (currentIsMulti && currentOrders.length >= USE_STREAMING_THRESHOLD) {
        const streamingResult = await processLargeStreamingBatch(currentOrders, sizeToUse, cacheKey, shouldUseCache, signal);
        if (streamingResult.status !== "fallbackNeeded") {
          return;
        }
      }
      const attemptStandard = async (): Promise<void> => {
        const currentLabelUrls = await fetchLabelUrls(currentOrders, signal);
        if (signal?.aborted) {
          return;
        }
        if (!currentLabelUrls.some(Boolean)) {
          setPdfUrl(null);
          return;
        }
        if (currentIsMulti) {
          await processMultiLabelStandard(currentLabelUrls, sizeToUse, cacheKey, shouldUseCache, signal);
        } else {
          const firstLabel = currentLabelUrls[0];
          if (firstLabel) {
            await processSingleLabel(firstLabel, sizeToUse, cacheKey, shouldUseCache, signal);
          }
        }
      };
      try {
        await attemptStandard();
      } catch (err) {
        if (isAborted(signal, err)) {
          return;
        }
        if (shouldRefreshPresignedLabelUrlsForError(err)) {
          clearCachedLabelUrls();
          await attemptStandard();
          return;
        }
        throw err;
      }
    },
    [clearCachedLabelUrls, fetchLabelUrls, processLargeStreamingBatch, processMultiLabelStandard, processSingleLabel]
  );

  const generatePackingSlipPdf = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      currentIsMulti: boolean,
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      if (signal?.aborted) {
        return;
      }
      if (currentIsMulti) {
        const slips = await generateAllSlips(currentOrders, sizeToUse);
        if (signal?.aborted) {
          return;
        }
        const merged = await SimplePdfMerger.mergePdfs(slips, signal);
        if (signal?.aborted) {
          return;
        }
        handleCaching(cacheKey, merged, shouldUseCache);
        return;
      }
      const firstOrder = currentOrders[0];
      if (firstOrder) {
        const slip = await generatePackingSlip(firstOrder, sizeToUse);
        if (signal?.aborted) {
          return;
        }
        handleCaching(cacheKey, slip, shouldUseCache);
      }
    },
    [generateAllSlips, generatePackingSlip, handleCaching]
  );

  const generateSingleSlip = useCallback(
    async (order: PurchaseOrder, paperSize: PaperSize, index: number): Promise<{ index: number; pdf: PDFDocument | null }> => {
      try {
        const options = getPackingSlipOptionsForOrder(order);
        const slipPdf = await PackingSlipGenerator.createPackingSlipInMemory(order, paperSize, order.promotionTemplate, options);
        return { index, pdf: slipPdf };
      } catch (err) {
        console.error(`Error generating slip for order ${order.purchaseOrderId}:`, extractErrorMessage(err));
        return { index, pdf: null };
      }
    },
    [getPackingSlipOptionsForOrder]
  );

  const generateSlipsForChunk = useCallback(
    async (
      ordersChunk: PurchaseOrder[],
      paperSize: PaperSize,
      chunkOffset: number
    ): Promise<Array<{ index: number; pdf: PDFDocument | null }>> => {
      const results: Array<{ index: number; pdf: PDFDocument | null }> = [];
      const concurrency = FIT_CONCURRENCY_CHUNKED;
      const batches = chunk(ordersChunk, concurrency);
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        if (!batch) {
          continue;
        }
        const batchPromises = batch.map((order, i) => {
          if (!order) {
            return Promise.resolve(null);
          }
          return generateSingleSlip(order, paperSize, chunkOffset + batchIndex * concurrency + i);
        });
        // Batches are bounded to limit concurrent API calls and keep result order.
        const batchResults = await Promise.all(batchPromises); // NOSONAR
        for (const result of batchResults) {
          if (result) {
            results.push(result);
          }
        }
      }
      return results.sort((a, b) => a.index - b.index);
    },
    [generateSingleSlip]
  );

  // createOrderIndexMap is imported from pdfGeneration utilities

  const fetchLabelUrlsForChunk = useCallback(
    async function* (
      ordersChunk: PurchaseOrder[],
      chunkOffset: number,
      signal?: AbortSignal
    ): AsyncGenerator<{ url: string; orderIndex: number }, void, unknown> {
      if (ordersChunk.length === 0) {
        return;
      }
      try {
        const { getBulkShippingLabelUrls } = await import("@client-shared/services/api/orders");
        const orderIds = ordersChunk.map((o) => ({ purchaseOrderId: o.purchaseOrderId, batchId: o.batchId }));
        const batches = createBatches(orderIds);
        const orderIndexMap = createOrderIndexMap(ordersChunk, chunkOffset);
        let hasBatchErrors = false;
        for (let i = 0; i < batches.length; i += API_BATCH_CONCURRENCY) {
          if (signal?.aborted) {
            return;
          }
          // Groups of batches run one after another to bound API concurrency and keep yield order.
          const results = await fetchBatchGroup(batches, i, getBulkShippingLabelUrls, signal); // NOSONAR
          hasBatchErrors = hasBatchErrors || results.some((r) => !r.success);
          // Must run before yielding: consumers start fitting each URL as soon as it is yielded.
          results.forEach((result) => rememberLabelUrlPoMappings(result.urlMap));
          yield* extractIndexedLabelUrls(results, orderIndexMap);
        }
        if (hasBatchErrors) {
          config.onErrorToast(t("components.ShippingDocumentsDialog.errorFetchingShippingLabels"));
        }
      } catch (err) {
        if (isAborted(signal, err)) {
          return;
        }
        console.error("Failed to fetch shipping label URLs for chunk:", err);
        config.onErrorToast(t("components.ShippingDocumentsDialog.errorFetchingShippingLabels"));
      }
    },
    [config, createBatches, rememberLabelUrlPoMappings, t]
  );

  const processChunkForCombinedPdf = useCallback(
    async (
      chunkOrders: PurchaseOrder[],
      chunkOffset: number,
      mergedPdf: PDFDocument,
      sizeToUse: PaperSize,
      serializedAddPage: (page: PDFPage) => Promise<void>,
      serializedFitPdfInMemoryFn?: FitPdfInMemoryFn,
      signal?: AbortSignal
    ): Promise<void> => {
      if (signal?.aborted) {
        throw new Error("Operation aborted");
      }
      const slipMap = new Map<number, PDFDocument>();
      try {
        const slipResults = await generateSlipsForChunk(chunkOrders, sizeToUse, chunkOffset);
        if (signal?.aborted) {
          throw new Error("Operation aborted");
        }
        for (const { index, pdf } of slipResults) {
          if (pdf) {
            slipMap.set(index, pdf);
          }
        }
        const labelUrlGenerator = fetchLabelUrlsForChunk(chunkOrders, chunkOffset, signal);
        type LabelUrlWithIndex = { url: string; orderIndex: number };
        const processLabelAndSlip = async (item: LabelUrlWithIndex): Promise<void> => {
          if (signal?.aborted) {
            return;
          }
          await processLabelAndSlipForCombined(item.url, {
            mergedPdf,
            slipMap,
            sizeToUse,
            orderIndex: item.orderIndex,
            serializedAddPage,
            serializedFitPdfInMemory: serializedFitPdfInMemoryFn,
            signal,
            poNumber: resolvePoNumberForUrl(item.url),
          });
        };
        await processUrlsWithConcurrency(labelUrlGenerator, processLabelAndSlip, { signal });
      } catch (chunkError) {
        if (signal?.aborted || (chunkError instanceof Error && chunkError.name === "AbortError")) {
          throw new Error("Operation aborted");
        }
        throw chunkError;
      } finally {
        slipMap.clear();
      }
    },
    [fetchLabelUrlsForChunk, generateSlipsForChunk, resolvePoNumberForUrl]
  );

  const processAllChunksStreaming = useCallback(
    async (params: {
      currentOrders: PurchaseOrder[];
      totalChunks: number;
      chunkSize: number;
      mergedPdf: PDFDocument;
      sizeToUse: PaperSize;
      serializedAddPage: (page: PDFPage) => Promise<void>;
      serializedFitPdfInMemory?: FitPdfInMemoryFn;
      signal?: AbortSignal;
    }): Promise<Array<{ chunkIndex: number; error: unknown }>> => {
      const { currentOrders, totalChunks, chunkSize, mergedPdf, sizeToUse, serializedAddPage, serializedFitPdfInMemory, signal } = params;
      const chunkErrors: Array<{ chunkIndex: number; error: unknown }> = [];
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        if (isAborted(signal)) {
          throw new Error("Operation aborted");
        }
        const chunkStart = chunkIndex * chunkSize;
        const chunkOrders = currentOrders.slice(chunkStart, chunkStart + chunkSize);
        try {
          // Chunks are appended to the merged PDF in order and bound browser memory.
          const chunkProcessing = processChunkForCombinedPdf(
            chunkOrders,
            chunkStart,
            mergedPdf,
            sizeToUse,
            serializedAddPage,
            serializedFitPdfInMemory,
            signal
          );
          await chunkProcessing; // NOSONAR
        } catch (chunkError) {
          if (isAborted(signal, chunkError)) {
            throw new Error("Operation aborted");
          }
          chunkErrors.push({ chunkIndex, error: chunkError });
          console.error(`Error processing chunk ${chunkIndex + 1}/${totalChunks}:`, chunkError);
        }
      }
      return chunkErrors;
    },
    [processChunkForCombinedPdf]
  );

  const saveMergedPdfAndCache = useCallback(
    async (mergedPdf: PDFDocument, cacheKey: string, shouldUseCache: boolean): Promise<void> => {
      if (mergedPdf.getPageCount() === 0) {
        throw new Error("Generated PDF had no pages. Please try again.");
      }
      const pdfBytes = await mergedPdf.save({ useObjectStreams: true });
      const blob = new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
      handleCaching(cacheKey, URL.createObjectURL(blob), shouldUseCache);
    },
    [handleCaching]
  );

  const generateCombinedPdfStreaming = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ): Promise<boolean> => {
      if (!currentOrders || currentOrders.length === 0) {
        throw new Error("Cannot generate combined PDF: no orders provided.");
      }
      if (isAborted(signal)) {
        return false;
      }
      try {
        const mergedPdf = await serializedPdfCreate();
        const chunkSize = currentOrders.length >= COMBINED_PDF_CHUNK_THRESHOLD ? COMBINED_PDF_CHUNK_SIZE_LARGE : COMBINED_PDF_CHUNK_SIZE;
        const totalChunks = Math.ceil(currentOrders.length / chunkSize);
        const serializedAddPage = createSerializedAddPage(mergedPdf);
        const serializedFitPdfInMemoryFn = createSerializedFitPdfInMemory();
        const chunkErrors = await processAllChunksStreaming({
          currentOrders,
          totalChunks,
          chunkSize,
          mergedPdf,
          sizeToUse,
          serializedAddPage,
          serializedFitPdfInMemory: serializedFitPdfInMemoryFn,
          signal,
        });
        if (isAborted(signal)) {
          return false;
        }
        if (chunkErrors.length > 0) {
          console.warn(`${chunkErrors.length} of ${totalChunks} chunks failed`);
          if (chunkErrors.length === totalChunks) {
            throw new Error(`All ${totalChunks} chunks failed`);
          }
        }
        await saveMergedPdfAndCache(mergedPdf, cacheKey, shouldUseCache);
        return true;
      } catch (err) {
        if (isAborted(signal, err)) {
          return false;
        }
        console.error("Streaming combined PDF merge failed, falling back:", err);
        config.onWarningToast(
          t("components.ShippingDocumentsDialog.streamingFallbackTitle"),
          t("components.ShippingDocumentsDialog.streamingFallbackDescription")
        );
        return false;
      }
    },
    [config, processAllChunksStreaming, saveMergedPdfAndCache, t]
  );

  // createInterleavedSequence is imported from pdfGeneration utilities

  const processMultiOrderCombinedPdfStandard = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      currentLabelUrls: string[],
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      const slips = await generateAllSlips(currentOrders, sizeToUse);
      if (isAborted(signal)) {
        return;
      }
      const fitted = await fitAllLabels(currentLabelUrls, sizeToUse, signal);
      if (isAborted(signal)) {
        return;
      }
      const merged = await SimplePdfMerger.mergePdfs(createInterleavedSequence(fitted, slips), signal);
      if (isAborted(signal)) {
        return;
      }
      handleCaching(cacheKey, merged, shouldUseCache);
    },
    [fitAllLabels, generateAllSlips, handleCaching]
  );

  const processSingleOrderCombinedPdfStandard = useCallback(
    async (
      firstOrder: PurchaseOrder,
      firstLabel: string | undefined,
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      const slip = await generatePackingSlip(firstOrder, sizeToUse);
      if (isAborted(signal)) {
        return;
      }
      const fitted = firstLabel
        ? await fitPdfToPaper(firstLabel, sizeToUse, PDF_MARGIN_DEFAULT, signal, resolvePoNumberForUrl(firstLabel))
        : undefined;
      if (isAborted(signal)) {
        return;
      }
      const merged = await SimplePdfMerger.mergePdfs(fitted ? [fitted, slip] : [slip], signal);
      if (isAborted(signal)) {
        return;
      }
      handleCaching(cacheKey, merged, shouldUseCache);
    },
    [generatePackingSlip, handleCaching, resolvePoNumberForUrl]
  );

  const generateCombinedPdfStandard = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      currentIsMulti: boolean,
      currentLabelUrls: string[],
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      if (isAborted(signal)) {
        return;
      }
      if (currentIsMulti) {
        await processMultiOrderCombinedPdfStandard(currentOrders, currentLabelUrls, sizeToUse, cacheKey, shouldUseCache, signal);
        return;
      }
      const firstOrder = currentOrders[0];
      if (firstOrder) {
        await processSingleOrderCombinedPdfStandard(firstOrder, currentLabelUrls[0], sizeToUse, cacheKey, shouldUseCache, signal);
      }
    },
    [processMultiOrderCombinedPdfStandard, processSingleOrderCombinedPdfStandard]
  );

  const generateCombinedPdfStandardWithRetry = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      currentIsMulti: boolean,
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      const currentLabelUrls = await fetchLabelUrls(currentOrders, signal);
      if (signal?.aborted) {
        return;
      }
      try {
        await generateCombinedPdfStandard(currentOrders, currentIsMulti, currentLabelUrls, sizeToUse, cacheKey, shouldUseCache, signal);
      } catch (err) {
        if (isAborted(signal, err)) {
          return;
        }
        if (shouldRefreshPresignedLabelUrlsForError(err)) {
          clearCachedLabelUrls();
          const retryLabelUrls = await fetchLabelUrls(currentOrders, signal);
          if (signal?.aborted) {
            return;
          }
          await generateCombinedPdfStandard(currentOrders, currentIsMulti, retryLabelUrls, sizeToUse, cacheKey, shouldUseCache, signal);
          return;
        }
        throw err;
      }
    },
    [clearCachedLabelUrls, fetchLabelUrls, generateCombinedPdfStandard]
  );

  const generateCombinedPdf = useCallback(
    async (
      currentOrders: PurchaseOrder[],
      currentIsMulti: boolean,
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      if (signal?.aborted) {
        return;
      }
      if (currentIsMulti && currentOrders.length >= USE_STREAMING_THRESHOLD) {
        const success = await generateCombinedPdfStreaming(currentOrders, sizeToUse, cacheKey, shouldUseCache, signal);
        if (signal?.aborted || success) {
          return;
        }
      }
      await generateCombinedPdfStandardWithRetry(currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
    },
    [generateCombinedPdfStandardWithRetry, generateCombinedPdfStreaming]
  );

  const generatePdfForTab = useCallback(
    async (
      tab: ShippingDocumentsTabType,
      currentOrders: PurchaseOrder[],
      currentIsMulti: boolean,
      sizeToUse: PaperSize,
      cacheKey: string,
      shouldUseCache: boolean,
      signal?: AbortSignal
    ) => {
      if (tab === "shipping-label") {
        await generateShippingLabelPdf(currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
        return;
      }
      if (tab === "packing-slip") {
        await generatePackingSlipPdf(currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
        return;
      }
      if (tab === "combined") {
        await generateCombinedPdf(currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
        return;
      }
      if (tab === "manifest") {
        await handleManifestPdf(sizeToUse, signal);
      }
    },
    [generateCombinedPdf, generatePackingSlipPdf, generateShippingLabelPdf, handleManifestPdf]
  );

  // ---------------------------------------------------------------------------
  // PDF initial generation & regeneration effect
  // ---------------------------------------------------------------------------

  const generateInitialPdf = useCallback(async () => {
    const requested = activeTab;
    const effective = !hasLabels && (requested === "shipping-label" || requested === "combined") ? "packing-slip" : requested;
    const signal = abortControllerRef.current?.signal;
    try {
      clearError(setError);
      const sizeToUse = selectedPaperSize;
      const currentOrders = processedOrders;
      const currentIsMulti = isMulti;
      const cacheKey = generateCacheKey(effective, currentOrders, sizeToUse, selectedGroupOptions, config.stampPoNumberOnLabels);
      const shouldUseCache = !isLargeDataset || currentOrders.length <= CACHE_USAGE_THRESHOLD;
      if (shouldUseCache) {
        const cachedPdf = getCachedPdf(cacheKey);
        if (cachedPdf) {
          setPdfUrlWithRevocation(cachedPdf);
          return;
        }
      }
      if (effective === "shipping-label") {
        await generateShippingLabelPdf(currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
        return;
      }
      if (effective === "packing-slip") {
        await generatePackingSlipPdf(currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
        return;
      }
      if (effective === "combined") {
        await generateCombinedPdf(currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
        return;
      }
      if (effective === "manifest") {
        await handleManifestPdf(sizeToUse, signal);
        return;
      }
    } catch (err) {
      if (signal?.aborted || (err instanceof Error && err.name === "AbortError")) {
        return;
      }
      setError(extractErrorMessage(err));
    }
  }, [
    activeTab,
    config.stampPoNumberOnLabels,
    generateCombinedPdf,
    generatePackingSlipPdf,
    generateShippingLabelPdf,
    getCachedPdf,
    handleManifestPdf,
    hasLabels,
    isLargeDataset,
    isMulti,
    processedOrders,
    selectedGroupOptions,
    selectedPaperSize,
    setPdfUrlWithRevocation,
  ]);

  useEffect(() => {
    generateInitialPdfRef.current = generateInitialPdf;
  }, [generateInitialPdf]);

  useEffect(() => {
    if (open && !pdfUrl) {
      const timeoutId = setTimeout(() => {
        void generateInitialPdfRef.current();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [open, pdfUrl]);

  // ---------------------------------------------------------------------------
  // Tab change
  // ---------------------------------------------------------------------------

  const handleCachedPdf = useCallback(
    (cachedPdf: string) => {
      setPdfUrl((prevPdfUrl) => {
        if (
          prevPdfUrl?.startsWith("blob:") &&
          prevPdfUrl !== cachedPdf &&
          prevPdfUrl !== currentDisplayedUrlRef.current &&
          !hasCachedUrl(prevPdfUrl)
        ) {
          pendingRevocationRef.current.add(prevPdfUrl);
        }
        return cachedPdf;
      });
      setChangingTab(null);
    },
    [hasCachedUrl]
  );

  const handleTabChange = useCallback(
    async (tab: ShippingDocumentsTabType = "packing-slip", overridePaperSize?: PaperSize): Promise<void> => {
      if (!tab) {
        console.error("handleTabChange called with undefined tab");
        return;
      }
      if (!overridePaperSize && activeTab === tab && pdfUrl) {
        return;
      }
      if (changingTab !== null && changingTab !== tab) {
        return;
      }
      if (!overridePaperSize) {
        setActiveTab(tab);
        activeTabRef.current = tab;
        setChangingTab(tab);
      }
      const signal = abortControllerRef.current?.signal;
      try {
        clearError(setError);
        const sizeToUse = overridePaperSize ?? selectedPaperSize;
        const currentOrders = processedOrders;
        const currentIsMulti = isMulti;
        const cacheKey = generateCacheKey(tab, currentOrders, sizeToUse, selectedGroupOptions, config.stampPoNumberOnLabels);
        const shouldUseCache = !isLargeDataset || currentOrders.length <= CACHE_USAGE_THRESHOLD;
        if (shouldUseCache) {
          const cachedPdf = getCachedPdf(cacheKey);
          if (cachedPdf) {
            handleCachedPdf(cachedPdf);
            return;
          }
        }
        await generatePdfForTab(tab, currentOrders, currentIsMulti, sizeToUse, cacheKey, shouldUseCache, signal);
      } catch (err) {
        if (signal?.aborted || (err instanceof Error && err.name === "AbortError")) {
          setChangingTab(null);
          return;
        }
        setError(extractErrorMessage(err));
        setChangingTab(null);
      } finally {
        if (activeTabRef.current === tab) {
          setChangingTab(null);
        }
      }
    },
    [
      activeTab,
      changingTab,
      config.stampPoNumberOnLabels,
      generatePdfForTab,
      getCachedPdf,
      handleCachedPdf,
      isLargeDataset,
      isMulti,
      pdfUrl,
      processedOrders,
      selectedGroupOptions,
      selectedPaperSize,
    ]
  );

  const handlePaperSizeChange = useCallback(
    async (size: PaperSize): Promise<void> => {
      setSelectedPaperSize(size);
      setChangingPaperSize(size);
      try {
        await handleTabChange(activeTabRef.current ?? "packing-slip", size);
      } finally {
        setChangingPaperSize(null);
      }
    },
    [handleTabChange]
  );

  const handleGroupOptionToggle = useCallback(
    (option: GroupOption) => {
      setChangingGroupBy(option);
      try {
        setSelectedGroupOptions((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(option)) {
            newSet.delete(option);
          } else {
            newSet.add(option);
          }
          return newSet;
        });
        clearCache();
        setPdfUrl(null);
      } finally {
        setChangingGroupBy(null);
      }
    },
    [clearCache]
  );

  // ---------------------------------------------------------------------------
  // Wrapper URL tracking
  // ---------------------------------------------------------------------------

  const batchId = useMemo(() => {
    if (normalizedOrders.length === 0) {
      return undefined;
    }
    const firstBatchId = normalizedOrders[0]?.batchId;
    if (!firstBatchId) {
      return undefined;
    }
    for (let i = 1; i < normalizedOrders.length; i++) {
      if (normalizedOrders[i]?.batchId !== firstBatchId) {
        return undefined;
      }
    }
    return firstBatchId;
  }, [normalizedOrders]);

  const filename = useMemo(
    () => generateShippingDocumentFilename(activeTab, config.vendorLocale, new Date(), batchId, !isMulti),
    [activeTab, batchId, config.vendorLocale, isMulti]
  );

  const pdfDisplayUrl = useMemo(() => {
    if (!pdfUrl) {
      return null;
    }
    return createPdfWrapperUrl(pdfUrl, filename);
  }, [pdfUrl, filename]);

  const showPreviewFallback = useMemo(
    () => Boolean(open && pdfDisplayUrl && failedPreviewUrl === pdfDisplayUrl),
    [open, pdfDisplayUrl, failedPreviewUrl]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const previousWrapperUrl = currentWrapperUrlRef.current;
    if (previousWrapperUrl && previousWrapperUrl !== pdfDisplayUrl && previousWrapperUrl.startsWith("blob:")) {
      pendingWrapperRevocationRef.current.add(previousWrapperUrl);
    }
    currentWrapperUrlRef.current = pdfDisplayUrl;
  }, [open, pdfDisplayUrl]);

  // ---------------------------------------------------------------------------
  // Iframe load / revocation callbacks
  // ---------------------------------------------------------------------------

  const handleIframeLoad = useCallback(
    (currentSrc: string) => {
      if (!pdfUrl) {
        return;
      }
      const isBlank = currentSrc === "" || currentSrc === "about:blank";
      if (!isBlank) {
        setFailedPreviewUrl(null);
      }
      currentDisplayedUrlRef.current = currentSrc;
      const capturedPdfUrl = pdfUrl;
      const capturedWrapperUrl = currentWrapperUrlRef.current;
      // Drain stale wrapper URLs synchronously once the new wrapper has actually
      // loaded — Chromium rejects navigations to revoked blob: URLs with
      // "Not allowed to load local resource", so we can't risk a fixed timer
      // firing while the iframe is still navigating to the new wrapper.
      if (capturedWrapperUrl && currentSrc === capturedWrapperUrl) {
        const pendingWrapperUrls = Array.from(pendingWrapperRevocationRef.current);
        for (const urlToRevoke of pendingWrapperUrls) {
          if (urlToRevoke !== capturedWrapperUrl && urlToRevoke.startsWith("blob:")) {
            URL.revokeObjectURL(urlToRevoke);
            pendingWrapperRevocationRef.current.delete(urlToRevoke);
          }
        }
      }
      // Inner PDF blob still gets a grace window — the embed inside the wrapper
      // continues fetching it after the wrapper iframe's load event fires.
      setTimeout(() => {
        if (currentSrc !== capturedPdfUrl && currentSrc !== capturedWrapperUrl) {
          return;
        }
        const pendingUrls = Array.from(pendingRevocationRef.current);
        for (const urlToRevoke of pendingUrls) {
          if (urlToRevoke !== currentSrc && urlToRevoke !== capturedPdfUrl && urlToRevoke.startsWith("blob:")) {
            URL.revokeObjectURL(urlToRevoke);
            pendingRevocationRef.current.delete(urlToRevoke);
          }
        }
      }, 1500);
    },
    [pdfUrl]
  );

  const handleIframeError = useCallback(() => {
    if (!pdfDisplayUrl) {
      return;
    }
    setFailedPreviewUrl(pdfDisplayUrl);
  }, [pdfDisplayUrl]);

  // ---------------------------------------------------------------------------
  // Download / open
  // ---------------------------------------------------------------------------

  const handleDownload = useCallback(() => {
    if (!pdfUrl) {
      return;
    }
    downloadBlobWithFilename(pdfUrl, filename).catch((err) => {
      console.error("Error downloading PDF:", err);
      setError("Failed to download PDF");
    });
  }, [filename, pdfUrl]);

  const handleOpenInNewTab = useCallback(() => {
    if (!pdfUrl) {
      return;
    }
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  }, [pdfUrl]);

  // ---------------------------------------------------------------------------
  // Button state helpers
  // ---------------------------------------------------------------------------

  const getPaperSizeButtonState = useCallback(
    (size: PaperSize): ButtonState => {
      if (changingPaperSize === size) {
        return "loading";
      }
      if (selectedPaperSize === size) {
        return "pressed";
      }
      return undefined;
    },
    [changingPaperSize, selectedPaperSize]
  );

  const getGroupByButtonState = useCallback(
    (option: GroupOption): ButtonState => {
      if (changingGroupBy === option) {
        return "loading";
      }
      if (selectedGroupOptions.has(option)) {
        return "pressed";
      }
      return undefined;
    },
    [changingGroupBy, selectedGroupOptions]
  );

  // ---------------------------------------------------------------------------
  // Tabs computation
  // ---------------------------------------------------------------------------

  const shouldShowManifest = useMemo(
    () => shouldShowManifestUtil(purchaseOrders, manifestPdfUrl, config.customerAccountCarrierDetails),
    [purchaseOrders, config.customerAccountCarrierDetails, manifestPdfUrl]
  );

  const tabs = useMemo(
    () =>
      buildTabs({
        hasLabels,
        hasCancelledOrder,
        orderCount: normalizedOrders.length,
        shouldShowManifestTab: shouldShowManifest,
        manifestPdfUrl,
        initialTab: initialTab ?? "packing-slip",
        t,
      }),
    [hasLabels, hasCancelledOrder, normalizedOrders.length, shouldShowManifest, t, manifestPdfUrl, initialTab]
  );

  const showGroupBy = useMemo(() => shouldShowGroupByUtil(isMulti, manifestPdfUrl, initialTab), [isMulti, manifestPdfUrl, initialTab]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    activeTab,
    selectedPaperSize,
    pdfUrl,
    pdfDisplayUrl,
    error,
    isLoading: open && !pdfUrl && !error,
    isChangingTab: changingTab !== null,
    isChangingPaperSize: changingPaperSize !== null,
    showPreviewFallback,
    isLargeDataset,
    filename,
    tabs,
    isMulti,
    showGroupBy,
    selectedGroupOptions,
    hasLabels,
    hasCancelledOrder,
    paperSizes: PAPER_SIZES,
    handleTabChange,
    handlePaperSizeChange,
    handleGroupOptionToggle,
    handleDownload,
    handleOpenInNewTab,
    handleClose,
    getPaperSizeButtonState,
    getGroupByButtonState,
    handleIframeLoad,
    handleIframeError,
  };
};
